/**
 * Shared file validation utilities — platform-agnostic (no Node.js / Expo deps).
 * Used by both mobile (Expo) and web (browser) clients.
 */

import { MAX_FILE_SIZE, isAllowedDocumentMimeType } from './file';

export interface FileValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Validate file size
 */
export function validateFileSize(fileSize: number): FileValidationResult {
  if (fileSize === 0) {
    return { valid: false, error: 'Le fichier est vide.' };
  }
  if (fileSize > MAX_FILE_SIZE) {
    const sizeMB = (fileSize / (1024 * 1024)).toFixed(1);
    return {
      valid: false,
      error: `Le fichier est trop volumineux (${sizeMB} Mo). La taille maximale est de 5 Mo.`,
    };
  }
  return { valid: true };
}

/**
 * Validate declared MIME type
 */
export function validateMimeType(declaredMimeType: string): FileValidationResult {
  const normalized = normalizeMimeType(declaredMimeType);
  if (!isAllowedDocumentMimeType(normalized)) {
    return {
      valid: false,
      error: `Type de fichier non accepté (${declaredMimeType}). Seuls les PDF et images (JPEG, PNG, HEIC) sont autorisés.`,
    };
  }
  return { valid: true };
}

/**
 * Detect MIME type from magic bytes (first 12 bytes of the file).
 */
export function detectMimeFromBytes(bytes: number[]): string | null {
  // PDF: %PDF
  if (matchesSignature(bytes, [0x25, 0x50, 0x44, 0x46])) {
    return 'application/pdf';
  }

  // PNG
  if (matchesSignature(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
    return 'image/png';
  }

  // JPEG
  if (matchesSignature(bytes, [0xff, 0xd8, 0xff])) {
    return 'image/jpeg';
  }

  // HEIC/HEIF: bytes 4-7 = "ftyp", then brand contains heic/heix/mif1
  if (bytes.length >= 12) {
    const ftyp =
      bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70;
    if (ftyp) {
      const brand = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11]);
      if (['heic', 'heix', 'hevc', 'mif1'].includes(brand)) {
        return 'image/heic';
      }
    }
  }

  return null;
}

/**
 * Verify magic bytes match the declared MIME type.
 */
export function validateMagicBytes(
  bytes: number[],
  declaredMime: string
): FileValidationResult {
  if (bytes.length < 3) {
    return { valid: false, error: 'Le fichier semble corrompu (trop petit).' };
  }

  const detectedMime = detectMimeFromBytes(bytes);

  if (!detectedMime) {
    return {
      valid: false,
      error: 'Le type réel du fichier ne correspond à aucun format accepté. Le fichier pourrait être corrompu ou renommé.',
    };
  }

  const declaredNorm = normalizeMimeType(declaredMime);
  const detectedNorm = normalizeMimeType(detectedMime);

  if (declaredNorm !== detectedNorm) {
    // Tolerate heic/heif interchangeable
    const heicTypes = ['image/heic', 'image/heif'];
    if (heicTypes.includes(declaredNorm) && heicTypes.includes(detectedNorm)) {
      return { valid: true };
    }

    return {
      valid: false,
      error: `Le contenu du fichier ne correspond pas à son type déclaré. Attendu : ${declaredNorm}, détecté : ${detectedNorm}.`,
    };
  }

  return { valid: true };
}

/** Extensions accepted per MIME type */
const EXTENSION_MAP: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/heic': ['.heic', '.heif'],
  'image/heif': ['.heic', '.heif'],
};

/**
 * Validate file extension matches its MIME type
 */
export function validateFileExtension(
  fileName: string,
  mimeType: string
): FileValidationResult {
  const ext = fileName.toLowerCase().match(/\.[^.]+$/)?.[0];
  if (!ext) {
    // No extension — acceptable for camera photos
    return { valid: true };
  }

  const allowed = EXTENSION_MAP[mimeType];
  if (allowed && !allowed.includes(ext)) {
    return {
      valid: false,
      error: `L'extension du fichier (${ext}) ne correspond pas au type ${mimeType}. Le fichier pourrait avoir été renommé.`,
    };
  }

  return { valid: true };
}

/**
 * Infer MIME type from file extension (fallback when picker doesn't return mimeType).
 */
export function inferMimeTypeFromExtension(fileName: string): string | null {
  const ext = fileName.toLowerCase().match(/\.[^.]+$/)?.[0];
  if (!ext) return null;

  const map: Record<string, string> = {
    '.pdf': 'application/pdf',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.heic': 'image/heic',
    '.heif': 'image/heif',
  };

  return map[ext] || null;
}

export function normalizeMimeType(mime: string): string {
  const lower = mime.toLowerCase().trim();
  if (lower === 'image/heif') return 'image/heic';
  return lower;
}

function matchesSignature(bytes: number[], signature: number[]): boolean {
  if (bytes.length < signature.length) return false;
  return signature.every((b, i) => bytes[i] === b);
}
